import React, { useState, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Upload, X, Plus, Trash2, Download, User, Mail, Phone, MapPin, Globe, Briefcase, GraduationCap, Star, Heart, Link2, Linkedin, Github, Twitter, Instagram, Facebook, Youtube, Camera } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const SOCIAL_ICONS = {
  linkedin: { icon: Linkedin, color: '#0A66C2', label: 'LinkedIn' },
  github: { icon: Github, color: '#333', label: 'GitHub' },
  twitter: { icon: Twitter, color: '#1DA1F2', label: 'Twitter/X' },
  instagram: { icon: Instagram, color: '#E4405F', label: 'Instagram' },
  facebook: { icon: Facebook, color: '#1877F2', label: 'Facebook' },
  youtube: { icon: Youtube, color: '#FF0000', label: 'YouTube' },
  website: { icon: Globe, color: '#10B981', label: 'Website' },
};

const detectSocial = (url) => {
  if (!url) return 'website';
  const u = url.toLowerCase();
  if (u.includes('linkedin')) return 'linkedin';
  if (u.includes('github')) return 'github';
  if (u.includes('twitter') || u.includes('x.com')) return 'twitter';
  if (u.includes('instagram')) return 'instagram';
  if (u.includes('facebook') || u.includes('fb.com')) return 'facebook';
  if (u.includes('youtube') || u.includes('youtu.be')) return 'youtube';
  return 'website';
};

const SECTION_COLORS = {
  personal: { bg: 'rgba(94,252,232,0.08)', color: '#5EFCE8' },
  about: { bg: 'rgba(96,165,250,0.08)', color: '#60A5FA' },
  work: { bg: 'rgba(251,191,36,0.08)', color: '#FBBF24' },
  education: { bg: 'rgba(168,85,247,0.08)', color: '#A855F7' },
  skills: { bg: 'rgba(34,197,94,0.08)', color: '#22C55E' },
  languages: { bg: 'rgba(59,130,246,0.08)', color: '#3B82F6' },
  hobbies: { bg: 'rgba(244,63,94,0.08)', color: '#F43F5E' },
  links: { bg: 'rgba(20,184,166,0.08)', color: '#14B8A6' },
};

const HOBBY_STYLES = [
  { bg: '#FEF2F2', color: '#991B1B', border: '#FECACA' },
  { bg: '#FFF7ED', color: '#9A3412', border: '#FED7AA' },
  { bg: '#FEF9C3', color: '#854D0E', border: '#FDE68A' },
  { bg: '#F0FDF4', color: '#166534', border: '#BBF7D0' },
  { bg: '#ECFDF5', color: '#065F46', border: '#A7F3D0' },
  { bg: '#F0F9FF', color: '#075985', border: '#BAE6FD' },
  { bg: '#EFF6FF', color: '#1E40AF', border: '#BFDBFE' },
  { bg: '#F5F3FF', color: '#5B21B6', border: '#DDD6FE' },
  { bg: '#FDF4FF', color: '#86198F', border: '#F5D0FE' },
  { bg: '#FFF1F2', color: '#9F1239', border: '#FECDD3' },
  { bg: '#FEFCE8', color: '#713F12', border: '#FEF08A' },
  { bg: '#F0FDFA', color: '#134E4A', border: '#99F6E4' },
  { bg: '#F8FAFC', color: '#334155', border: '#CBD5E1' },
  { bg: '#FFFBEB', color: '#92400E', border: '#FDE68A' },
  { bg: '#FEF2F2', color: '#7F1D1D', border: '#FCA5A5' },
];

const skillStyle = (tpl) => {
  const base = { padding: '5px 14px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, display: 'inline-block', lineHeight: '1.2', verticalAlign: 'middle', boxSizing: 'border-box', fontFamily: "'Segoe UI', Arial, sans-serif" };
  if (tpl === 'modern') return { ...base, background: 'rgba(255,255,255,0.12)', borderRadius: '12px', fontSize: '10px', color: '#fff' };
  if (tpl === 'creative') return { ...base, background: '#EDE9FE', color: '#6D28D9', border: '1px solid #DDD6FE' };
  return { ...base, background: '#f1f5f9', color: '#1e3a5f', border: '1px solid #e2e8f0' };
};

const hobbyStyle = (idx, tpl) => {
  const h = HOBBY_STYLES[idx % HOBBY_STYLES.length];
  const base = { padding: '5px 14px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, display: 'inline-block', lineHeight: '1.2', verticalAlign: 'middle', boxSizing: 'border-box', fontFamily: "'Segoe UI', Arial, sans-serif" };
  if (tpl === 'modern') return { ...base, background: 'rgba(255,255,255,0.08)', borderRadius: '12px', fontSize: '10px', color: '#fff' };
  if (tpl === 'creative') return { ...base, background: h.bg, color: h.color, border: `1px solid ${h.border}` };
  return { ...base, background: h.bg, color: h.color, border: `1px solid ${h.border}` };
};

const ResumeBuilder = () => {
  const { t } = useLanguage();
  const previewRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [template, setTemplate] = useState('classic');

  const [photo, setPhoto] = useState(null);
  const [fullName, setFullName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [aboutMe, setAboutMe] = useState('');
  const [languages, setLanguages] = useState([]);
  const [newLang, setNewLang] = useState('');
  const [newLangLevel, setNewLangLevel] = useState(50);
  const [hobbies, setHobbies] = useState([]);
  const [newHobby, setNewHobby] = useState('');
  const [socialLinks, setSocialLinks] = useState([]);
  const [newSocialUrl, setNewSocialUrl] = useState('');
  const [newSocialText, setNewSocialText] = useState('');
  const [workExp, setWorkExp] = useState([]);
  const [education, setEducation] = useState([]);
  const [skills, setSkills] = useState([]);
  const [newSkill, setNewSkill] = useState('');

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setPhoto(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const addLanguage = () => { if (!newLang.trim()) return; setLanguages([...languages, { name: newLang.trim(), level: newLangLevel }]); setNewLang(''); setNewLangLevel(50); };
  const addHobby = () => { if (!newHobby.trim()) return; setHobbies([...hobbies, newHobby.trim()]); setNewHobby(''); };
  const addSocialLink = () => { if (!newSocialUrl.trim()) return; const url = newSocialUrl.trim().startsWith('http') ? newSocialUrl.trim() : `https://${newSocialUrl.trim()}`; setSocialLinks([...socialLinks, { url, text: newSocialText.trim() || url }]); setNewSocialUrl(''); setNewSocialText(''); };
  const addWorkExp = () => { setWorkExp([...workExp, { company: '', position: '', country: '', fromYear: '', toYear: '' }]); };
  const updateWorkExp = (i, f, v) => { const u = [...workExp]; u[i] = { ...u[i], [f]: v }; setWorkExp(u); };
  const addEducation = () => { setEducation([...education, { institution: '', city: '', fromYear: '', toYear: '' }]); };
  const updateEducation = (i, f, v) => { const u = [...education]; u[i] = { ...u[i], [f]: v }; setEducation(u); };
  const addSkill = () => { if (!newSkill.trim()) return; setSkills([...skills, newSkill.trim()]); setNewSkill(''); };

  const downloadPDF = async () => {
    if (!previewRef.current) return;
    setDownloading(true);
    try {
      const el = previewRef.current;
      const wrap = el.parentElement;
      const origWrapStyle = wrap.style.cssText;
      const origElStyle = el.style.cssText;

      wrap.style.maxHeight = 'none';
      wrap.style.overflow = 'visible';
      wrap.style.position = 'relative';
      wrap.style.height = 'auto';
      el.classList.add('rb-pdf-capture');
      const modernInner = el.querySelector('.rbp-modern');
      if (modernInner) modernInner.classList.add('rb-pdf-capture');

      await new Promise(r => setTimeout(r, 200));

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: -window.scrollY,
      });

      el.classList.remove('rb-pdf-capture');
      if (modernInner) modernInner.classList.remove('rb-pdf-capture');
      wrap.style.cssText = origWrapStyle;
      el.style.cssText = origElStyle;

      // Crop out bottom white space
      const ctx = canvas.getContext('2d');
      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imgData.data;
      let lastContentRow = 0;
      for (let y = canvas.height - 1; y >= 0; y--) {
        let hasContent = false;
        for (let x = 0; x < canvas.width; x += 4) {
          const idx = (y * canvas.width + x) * 4;
          if (pixels[idx] < 250 || pixels[idx + 1] < 250 || pixels[idx + 2] < 250) {
            hasContent = true;
            break;
          }
        }
        if (hasContent) { lastContentRow = y; break; }
      }

      // Add 40px padding below content
      const cropH = Math.min(lastContentRow + 80, canvas.height);
      const croppedCanvas = document.createElement('canvas');
      croppedCanvas.width = canvas.width;
      croppedCanvas.height = cropH;
      const croppedCtx = croppedCanvas.getContext('2d');
      croppedCtx.drawImage(canvas, 0, 0, canvas.width, cropH, 0, 0, canvas.width, cropH);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const contentW = pdfW - 16;
      const contentH = (croppedCanvas.height * contentW) / croppedCanvas.width;
      const croppedImgData = croppedCanvas.toDataURL('image/png');

      if (contentH + 16 <= pdfH) {
        pdf.addImage(croppedImgData, 'PNG', 8, 8, contentW, contentH);
      } else {
        let y = 0;
        const pageH = pdfH - 16;
        while (y < contentH) {
          if (y > 0) pdf.addPage();
          pdf.addImage(croppedImgData, 'PNG', 8, 8 - y, contentW, contentH);
          y += pageH;
        }
      }

      pdf.save(`${fullName || 'resume'}.pdf`);
    } catch (err) { console.error('PDF error:', err); }
    finally { setDownloading(false); }
  };

  const SectionHeader = ({ icon: Icon, title, colorKey }) => {
    const c = SECTION_COLORS[colorKey];
    return (
      <div className="rb-section-header">
        <div className="rb-section-icon" style={{ background: c.bg, color: c.color }}><Icon size={16} /></div>
        <span className="rb-section-title">{title}</span>
      </div>
    );
  };

  return (
    <div className="rb-container" data-testid="resume-builder">
      <div className="rb-layout">
        {/* FORM */}
        <div className="rb-form">

          {/* Personal Info */}
          <div className="rb-section">
            <SectionHeader icon={User} title={t('rbPersonal') || 'Personal Information'} colorKey="personal" />
            <label className="rb-photo-upload">
              {photo ? (
                <div className="rb-photo-preview-wrap">
                  <img src={photo} alt="preview" className="rb-photo-preview" />
                  <button type="button" className="rb-photo-remove" onClick={(e) => { e.preventDefault(); setPhoto(null); }}><X size={12} /></button>
                </div>
              ) : (
                <div className="rb-photo-placeholder"><Camera size={20} /><span>{t('rbUploadPhoto') || 'Upload Photo'}</span></div>
              )}
              <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} data-testid="rb-photo-input" />
            </label>
            <div style={{ height: 10 }} />
            <input value={fullName} onChange={e => setFullName(e.target.value)} placeholder={t('rbFullName') || 'Full Name'} className="rb-input" data-testid="rb-fullname" />
            <input value={jobTitle} onChange={e => setJobTitle(e.target.value)} placeholder={t('rbJobTitle') || 'Job Title / Position'} className="rb-input" data-testid="rb-jobtitle" />
            <div className="rb-row">
              <input value={mobile} onChange={e => setMobile(e.target.value)} placeholder={t('rbMobile') || 'Phone'} className="rb-input" data-testid="rb-mobile" />
              <input value={email} onChange={e => setEmail(e.target.value)} placeholder={t('rbEmail') || 'Email'} className="rb-input" data-testid="rb-email" />
            </div>
            <input value={address} onChange={e => setAddress(e.target.value)} placeholder={t('rbAddress') || 'Address'} className="rb-input" data-testid="rb-address" />
          </div>

          {/* About Me */}
          <div className="rb-section">
            <SectionHeader icon={User} title={t('rbAboutMe') || 'About Me'} colorKey="about" />
            <textarea value={aboutMe} onChange={e => setAboutMe(e.target.value)} placeholder={t('rbAboutMePh') || 'Write a short professional summary about yourself...'} className="rb-textarea" rows={4} data-testid="rb-about" />
          </div>

          {/* Work Experience */}
          <div className="rb-section">
            <SectionHeader icon={Briefcase} title={t('rbWorkExp') || 'Work Experience'} colorKey="work" />
            {workExp.map((w, i) => (
              <div key={i} className="rb-card">
                <button className="rb-card-remove" onClick={() => setWorkExp(workExp.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
                <input value={w.position} onChange={e => updateWorkExp(i, 'position', e.target.value)} placeholder={t('rbPosition') || 'Position'} className="rb-input rb-input-sm" />
                <input value={w.company} onChange={e => updateWorkExp(i, 'company', e.target.value)} placeholder={t('rbCompany') || 'Company'} className="rb-input rb-input-sm" />
                <input value={w.country} onChange={e => updateWorkExp(i, 'country', e.target.value)} placeholder={t('rbCountry') || 'Country'} className="rb-input rb-input-sm" />
                <div className="rb-row">
                  <input value={w.fromYear} onChange={e => updateWorkExp(i, 'fromYear', e.target.value)} placeholder={t('rbFromYear') || 'From (year)'} className="rb-input rb-input-sm" />
                  <input value={w.toYear} onChange={e => updateWorkExp(i, 'toYear', e.target.value)} placeholder={t('rbToYear') || 'To (year)'} className="rb-input rb-input-sm" />
                </div>
              </div>
            ))}
            <button className="rb-add-btn" onClick={addWorkExp}><Plus size={15} /> {t('rbAddWork') || 'Add Experience'}</button>
          </div>

          {/* Education */}
          <div className="rb-section">
            <SectionHeader icon={GraduationCap} title={t('rbEducation') || 'Education'} colorKey="education" />
            {education.map((ed, i) => (
              <div key={i} className="rb-card">
                <button className="rb-card-remove" onClick={() => setEducation(education.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
                <input value={ed.institution} onChange={e => updateEducation(i, 'institution', e.target.value)} placeholder={t('rbInstitution') || 'Institution'} className="rb-input rb-input-sm" />
                <input value={ed.city} onChange={e => updateEducation(i, 'city', e.target.value)} placeholder={t('rbCity') || 'City'} className="rb-input rb-input-sm" />
                <div className="rb-row">
                  <input value={ed.fromYear} onChange={e => updateEducation(i, 'fromYear', e.target.value)} placeholder={t('rbFromYear') || 'From'} className="rb-input rb-input-sm" />
                  <input value={ed.toYear} onChange={e => updateEducation(i, 'toYear', e.target.value)} placeholder={t('rbToYear') || 'To'} className="rb-input rb-input-sm" />
                </div>
              </div>
            ))}
            <button className="rb-add-btn" onClick={addEducation}><Plus size={15} /> {t('rbAddEdu') || 'Add Education'}</button>
          </div>

          {/* Skills */}
          <div className="rb-section">
            <SectionHeader icon={Star} title={t('rbSkills') || 'Skills'} colorKey="skills" />
            <div className="rb-inline-add">
              <input value={newSkill} onChange={e => setNewSkill(e.target.value)} onKeyDown={e => e.key === 'Enter' && addSkill()} placeholder={t('rbSkillPh') || 'e.g. React, Photoshop'} className="rb-input rb-input-sm" data-testid="rb-skill-input" />
              <button className="rb-add-btn-sm" onClick={addSkill}><Plus size={15} /></button>
            </div>
            <div className="rb-tags">{skills.map((s, i) => (
              <span key={i} className="rb-tag">{s}<button onClick={() => setSkills(skills.filter((_, j) => j !== i))}><X size={12} /></button></span>
            ))}</div>
          </div>

          {/* Languages */}
          <div className="rb-section">
            <SectionHeader icon={Globe} title={t('rbLanguages') || 'Languages'} colorKey="languages" />
            <div className="rb-inline-add">
              <input value={newLang} onChange={e => setNewLang(e.target.value)} onKeyDown={e => e.key === 'Enter' && addLanguage()} placeholder={t('rbLangPh') || 'e.g. English'} className="rb-input rb-input-sm" data-testid="rb-lang-input" />
              <input type="range" min={0} max={100} value={newLangLevel} onChange={e => setNewLangLevel(Number(e.target.value))} className="rb-range" />
              <span className="rb-range-val">{newLangLevel}%</span>
              <button className="rb-add-btn-sm" onClick={addLanguage}><Plus size={15} /></button>
            </div>
            {languages.map((l, i) => (
              <div key={i} className="rb-lang-item">
                <span style={{ minWidth: 70 }}>{l.name}</span>
                <div className="rb-lang-bar"><div className="rb-lang-fill" style={{ width: `${l.level}%` }}></div></div>
                <span className="rb-lang-pct">{l.level}%</span>
                <button className="rb-remove-sm" onClick={() => setLanguages(languages.filter((_, j) => j !== i))}><X size={12} /></button>
              </div>
            ))}
          </div>

          {/* Hobbies */}
          <div className="rb-section">
            <SectionHeader icon={Heart} title={t('rbHobbies') || 'Hobbies'} colorKey="hobbies" />
            <div className="rb-inline-add">
              <input value={newHobby} onChange={e => setNewHobby(e.target.value)} onKeyDown={e => e.key === 'Enter' && addHobby()} placeholder={t('rbHobbyPh') || 'e.g. Photography'} className="rb-input rb-input-sm" data-testid="rb-hobby-input" />
              <button className="rb-add-btn-sm" onClick={addHobby}><Plus size={15} /></button>
            </div>
            <div className="rb-tags">{hobbies.map((h, i) => (
              <span key={i} className="rb-tag rb-tag-hobby">{h}<button onClick={() => setHobbies(hobbies.filter((_, j) => j !== i))}><X size={12} /></button></span>
            ))}</div>
          </div>

          {/* Social Links */}
          <div className="rb-section">
            <SectionHeader icon={Link2} title={t('rbSocialLinks') || 'Social Links'} colorKey="links" />
            <input value={newSocialUrl} onChange={e => setNewSocialUrl(e.target.value)} placeholder="https://linkedin.com/in/..." className="rb-input rb-input-sm" data-testid="rb-social-input" />
            <div className="rb-inline-add">
              <input value={newSocialText} onChange={e => setNewSocialText(e.target.value)} onKeyDown={e => e.key === 'Enter' && addSocialLink()} placeholder={t('rbLinkText') || 'Display text (e.g. My Portfolio)'} className="rb-input rb-input-sm" data-testid="rb-social-text" />
              <button className="rb-add-btn-sm" onClick={addSocialLink}><Plus size={15} /></button>
            </div>
            {socialLinks.map((link, i) => {
              const type = detectSocial(link.url);
              const SIcon = SOCIAL_ICONS[type].icon;
              return (
                <div key={i} className="rb-social-item">
                  <SIcon size={16} style={{ color: SOCIAL_ICONS[type].color }} />
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{link.text}</div>
                    <div className="rb-social-url">{link.url}</div>
                  </div>
                  <button className="rb-remove-sm" onClick={() => setSocialLinks(socialLinks.filter((_, j) => j !== i))}><X size={12} /></button>
                </div>
              );
            })}
          </div>

          {/* Template Selector */}
          <div className="rb-templates">
            <div className="rb-section-header">
              <div className="rb-section-icon" style={{ background: 'rgba(168,85,247,0.08)', color: '#A855F7' }}><Star size={16} /></div>
              <span className="rb-section-title">{t('rbDesignStyle') || 'Design Style'}</span>
            </div>
            <div className="rb-template-grid">
              {[
                { id: 'classic', label: t('rbClassic') || 'Classic', color: '#1e3a5f' },
                { id: 'modern', label: t('rbModern') || 'Modern', color: '#059669' },
                { id: 'creative', label: t('rbCreative') || 'Creative', color: '#7C3AED' },
              ].map(tp => (
                <button
                  key={tp.id}
                  className={`rb-template-btn ${template === tp.id ? 'rb-template-active' : ''}`}
                  onClick={() => setTemplate(tp.id)}
                  data-testid={`rb-template-${tp.id}`}
                  style={{ '--tmpl-color': tp.color }}
                >
                  <div className="rb-template-preview-mini" style={{ borderColor: tp.color }}>
                    <div style={{ width: '100%', height: 3, background: tp.color, borderRadius: 1 }} />
                    <div style={{ width: '60%', height: 2, background: '#ddd', marginTop: 3, borderRadius: 1 }} />
                    <div style={{ width: '40%', height: 2, background: '#ddd', marginTop: 2, borderRadius: 1 }} />
                    <div style={{ width: '80%', height: 2, background: '#eee', marginTop: 3, borderRadius: 1 }} />
                  </div>
                  <span>{tp.label}</span>
                </button>
              ))}
            </div>
          </div>

          <button className="rb-download-btn" onClick={downloadPDF} disabled={downloading} data-testid="rb-download-pdf">
            <Download size={18} />
            {downloading ? (t('rbDownloading') || 'Generating PDF...') : (t('rbDownloadPDF') || 'Download as PDF')}
          </button>
        </div>

        {/* PREVIEW */}
        <div className="rb-preview-wrap">
          <div className={`rb-preview rb-tpl-${template}`} ref={previewRef} data-testid="rb-preview">

            {/* ===== CLASSIC TEMPLATE ===== */}
            {template === 'classic' && (
              <>
                <div className="rbp-header">
                  {photo && <div className="rbp-header-left"><img src={photo} alt="avatar" className="rbp-avatar" /></div>}
                  <div className="rbp-header-right">
                    <h1 className="rbp-name">{fullName || (t('rbYourName') || 'Your Name')}</h1>
                    {jobTitle && <p className="rbp-jobtitle">{jobTitle}</p>}
                    <div className="rbp-contact-row">
                      {mobile && <span><Phone size={11} /> {mobile}</span>}
                      {email && <span><Mail size={11} /> {email}</span>}
                      {address && <span><MapPin size={11} /> {address}</span>}
                    </div>
                  </div>
                </div>
                {aboutMe && <div className="rbp-section"><h2 className="rbp-section-title"><User size={13} /> {t('rbAboutMe') || 'About Me'}</h2><p className="rbp-about-text">{aboutMe}</p></div>}
                {workExp.length > 0 && workExp.some(w => w.position || w.company) && <div className="rbp-section"><h2 className="rbp-section-title"><Briefcase size={13} /> {t('rbWorkExp') || 'Work Experience'}</h2>{workExp.filter(w => w.position || w.company).map((w, i) => <div key={i} className="rbp-entry"><div className="rbp-entry-header"><strong>{w.position}</strong>{w.fromYear && <span className="rbp-dates">{w.fromYear} — {w.toYear || t('rbPresent') || 'Present'}</span>}</div><div className="rbp-entry-sub">{w.company}{w.country ? `, ${w.country}` : ''}</div></div>)}</div>}
                {education.length > 0 && education.some(e => e.institution) && <div className="rbp-section"><h2 className="rbp-section-title"><GraduationCap size={13} /> {t('rbEducation') || 'Education'}</h2>{education.filter(e => e.institution).map((ed, i) => <div key={i} className="rbp-entry"><div className="rbp-entry-header"><strong>{ed.institution}</strong>{ed.fromYear && <span className="rbp-dates">{ed.fromYear} — {ed.toYear || t('rbPresent') || 'Present'}</span>}</div>{ed.city && <div className="rbp-entry-sub">{ed.city}</div>}</div>)}</div>}
                {skills.length > 0 && <div className="rbp-section"><h2 className="rbp-section-title"><Star size={13} /> {t('rbSkills') || 'Skills'}</h2><div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>{skills.map((s, i) => <div key={i} style={{ height: '28px', lineHeight: '28px', paddingLeft: '14px', paddingRight: '14px', backgroundColor: '#f1f5f9', color: '#1e3a5f', borderRadius: '6px', fontSize: '11px', fontWeight: 700, textAlign: 'center', fontFamily: "'Segoe UI', Arial, sans-serif", border: '1px solid #e2e8f0', boxSizing: 'border-box' }}>{s}</div>)}</div></div>}
                {languages.length > 0 && <div className="rbp-section"><h2 className="rbp-section-title"><Globe size={13} /> {t('rbLanguages') || 'Languages'}</h2><table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>{languages.map((l, i) => <tr key={i}><td style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b', padding: '4px 10px 4px 0', width: '80px', verticalAlign: 'middle', fontFamily: "'Segoe UI', Arial, sans-serif" }}>{l.name}</td><td style={{ verticalAlign: 'middle', padding: '4px 0' }}><div style={{ height: '6px', backgroundColor: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}><div style={{ height: '100%', width: `${l.level}%`, backgroundColor: '#1e3a5f', borderRadius: '3px' }} /></div></td><td style={{ fontSize: '11px', color: '#94a3b8', width: '36px', textAlign: 'right', verticalAlign: 'middle', fontFamily: "'Segoe UI', Arial, sans-serif", padding: '4px 0 4px 8px' }}>{l.level}%</td></tr>)}</tbody></table></div>}
                {hobbies.length > 0 && <div className="rbp-section"><h2 className="rbp-section-title"><Heart size={13} /> {t('rbHobbies') || 'Hobbies'}</h2><div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>{hobbies.map((h, i) => { const hs = HOBBY_STYLES[i % HOBBY_STYLES.length]; return <div key={i} style={{ height: '28px', lineHeight: '28px', paddingLeft: '14px', paddingRight: '14px', backgroundColor: hs.bg, color: hs.color, borderRadius: '20px', fontSize: '11px', fontWeight: 700, textAlign: 'center', fontFamily: "'Segoe UI', Arial, sans-serif", border: `1px solid ${hs.border}`, boxSizing: 'border-box' }}>{h}</div>; })}</div></div>}
                {socialLinks.length > 0 && <div className="rbp-section"><h2 className="rbp-section-title"><Link2 size={13} /> {t('rbSocialLinks') || 'Links'}</h2><table style={{ borderCollapse: 'collapse' }}><tbody>{socialLinks.map((link, i) => { const type = detectSocial(link.url); const SIcon = SOCIAL_ICONS[type].icon; return <tr key={i}><td style={{ padding: '3px 8px 3px 0', verticalAlign: 'middle' }}><SIcon size={13} style={{ color: SOCIAL_ICONS[type].color }} /></td><td style={{ fontSize: '12px', fontWeight: 500, color: '#1e293b', fontFamily: "'Segoe UI', Arial, sans-serif", verticalAlign: 'middle', padding: '3px 0' }}>{link.text}</td></tr>; })}</tbody></table></div>}
              </>
            )}

            {/* ===== MODERN TEMPLATE (2-column with sidebar) ===== */}
            {template === 'modern' && (
              <div className="rbp-modern">
                <div className="rbp-modern-sidebar">
                  {/* Decorative shapes */}
                  <div className="rbp-modern-shape rbp-shape-1" />
                  <div className="rbp-modern-shape rbp-shape-2" />
                  {photo && <img src={photo} alt="avatar" className="rbp-modern-avatar" />}
                  <h1 className="rbp-modern-name">{fullName || (t('rbYourName') || 'Your Name')}</h1>
                  {jobTitle && <p className="rbp-modern-jobtitle">{jobTitle}</p>}
                  <div className="rbp-modern-contact">
                    {mobile && <div><Phone size={12} /> {mobile}</div>}
                    {email && <div><Mail size={12} /> {email}</div>}
                    {address && <div><MapPin size={12} /> {address}</div>}
                  </div>
                  {languages.length > 0 && (
                    <div className="rbp-modern-sb-section">
                      <h3><Globe size={12} /> {t('rbLanguages') || 'Languages'}</h3>
                      {languages.map((l, i) => (
                        <div key={i} style={{ marginBottom: '6px' }}>
                          <span style={{ fontSize: '11px', display: 'block', marginBottom: '3px', color: '#fff', lineHeight: '1.4' }}>{l.name}</span>
                          <div style={{ height: '4px', background: 'rgba(255,255,255,0.15)', borderRadius: '2px', overflow: 'hidden' }}><div style={{ height: '100%', width: `${l.level}%`, background: '#6EE7B7', borderRadius: '2px' }} /></div>
                        </div>
                      ))}
                    </div>
                  )}
                  {skills.length > 0 && (
                    <div className="rbp-modern-sb-section">
                      <h3><Star size={12} /> {t('rbSkills') || 'Skills'}</h3>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>{skills.map((s, i) => <div key={i} style={{ padding: '4px 12px', backgroundColor: 'rgba(255,255,255,0.12)', borderRadius: '12px', fontSize: '10px', fontWeight: 600, color: '#fff', lineHeight: '1.3' }}>{s}</div>)}</div>
                    </div>
                  )}
                  {hobbies.length > 0 && (
                    <div className="rbp-modern-sb-section">
                      <h3><Heart size={12} /> {t('rbHobbies') || 'Hobbies'}</h3>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>{hobbies.map((h, i) => <div key={i} style={{ padding: '4px 12px', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '12px', fontSize: '10px', color: '#fff', lineHeight: '1.3' }}>{h}</div>)}</div>
                    </div>
                  )}
                  {socialLinks.length > 0 && (
                    <div className="rbp-modern-sb-section">
                      <h3><Link2 size={12} /> {t('rbSocialLinks') || 'Links'}</h3>
                      {socialLinks.map((link, i) => { const type = detectSocial(link.url); const SIcon = SOCIAL_ICONS[type].icon; return <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', marginBottom: '4px', color: '#fff', lineHeight: '1.4' }}><SIcon size={12} /><span>{link.text}</span></div>; })}
                    </div>
                  )}
                </div>
                <div className="rbp-modern-main">
                  {aboutMe && <div className="rbp-modern-section"><h2><User size={14} /> {t('rbAboutMe') || 'About Me'}</h2><p>{aboutMe}</p></div>}
                  {workExp.length > 0 && workExp.some(w => w.position || w.company) && (
                    <div className="rbp-modern-section"><h2><Briefcase size={14} /> {t('rbWorkExp') || 'Work Experience'}</h2>
                      {workExp.filter(w => w.position || w.company).map((w, i) => (
                        <div key={i} className="rbp-modern-entry"><div className="rbp-modern-entry-top"><strong>{w.position}</strong><span>{w.fromYear}{w.fromYear ? ' — ' : ''}{w.toYear || (w.fromYear ? (t('rbPresent') || 'Present') : '')}</span></div><div className="rbp-modern-entry-sub">{w.company}{w.country ? `, ${w.country}` : ''}</div></div>
                      ))}
                    </div>
                  )}
                  {education.length > 0 && education.some(e => e.institution) && (
                    <div className="rbp-modern-section"><h2><GraduationCap size={14} /> {t('rbEducation') || 'Education'}</h2>
                      {education.filter(e => e.institution).map((ed, i) => (
                        <div key={i} className="rbp-modern-entry"><div className="rbp-modern-entry-top"><strong>{ed.institution}</strong><span>{ed.fromYear}{ed.fromYear ? ' — ' : ''}{ed.toYear || (ed.fromYear ? (t('rbPresent') || 'Present') : '')}</span></div>{ed.city && <div className="rbp-modern-entry-sub">{ed.city}</div>}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ===== CREATIVE TEMPLATE (Bold header + shapes) ===== */}
            {template === 'creative' && (
              <>
                <div className="rbp-creative-header">
                  <div className="rbp-creative-shape rbp-cs-1" />
                  <div className="rbp-creative-shape rbp-cs-2" />
                  <div className="rbp-creative-shape rbp-cs-3" />
                  <div className="rbp-creative-header-content">
                    {photo && <img src={photo} alt="avatar" className="rbp-creative-avatar" />}
                    <div>
                      <h1 className="rbp-creative-name">{fullName || (t('rbYourName') || 'Your Name')}</h1>
                      {jobTitle && <p className="rbp-creative-jobtitle">{jobTitle}</p>}
                      <div className="rbp-creative-contact">
                        {mobile && <span><Phone size={11} /> {mobile}</span>}
                        {email && <span><Mail size={11} /> {email}</span>}
                        {address && <span><MapPin size={11} /> {address}</span>}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="rbp-creative-body">
                  <div className="rbp-creative-left">
                    {aboutMe && <div className="rbp-cr-section"><h2>{t('rbAboutMe') || 'About Me'}</h2><p>{aboutMe}</p></div>}
                    {workExp.length > 0 && workExp.some(w => w.position || w.company) && (
                      <div className="rbp-cr-section"><h2>{t('rbWorkExp') || 'Work Experience'}</h2>
                        {workExp.filter(w => w.position || w.company).map((w, i) => (
                          <div key={i} className="rbp-cr-entry"><div className="rbp-cr-dot" /><div><strong>{w.position}</strong><div className="rbp-cr-meta">{w.company}{w.country ? `, ${w.country}` : ''} {w.fromYear ? `| ${w.fromYear} — ${w.toYear || (t('rbPresent') || 'Present')}` : ''}</div></div></div>
                        ))}
                      </div>
                    )}
                    {education.length > 0 && education.some(e => e.institution) && (
                      <div className="rbp-cr-section"><h2>{t('rbEducation') || 'Education'}</h2>
                        {education.filter(e => e.institution).map((ed, i) => (
                          <div key={i} className="rbp-cr-entry"><div className="rbp-cr-dot" /><div><strong>{ed.institution}</strong>{ed.city && <div className="rbp-cr-meta">{ed.city} {ed.fromYear ? `| ${ed.fromYear} — ${ed.toYear || (t('rbPresent') || 'Present')}` : ''}</div>}</div></div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="rbp-creative-right">
                    {skills.length > 0 && <div className="rbp-cr-section"><h2>{t('rbSkills') || 'Skills'}</h2><div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>{skills.map((s, i) => <div key={i} style={{ height: '28px', lineHeight: '28px', paddingLeft: '14px', paddingRight: '14px', backgroundColor: '#EDE9FE', color: '#6D28D9', borderRadius: '6px', fontSize: '11px', fontWeight: 700, textAlign: 'center', border: '1px solid #DDD6FE', boxSizing: 'border-box' }}>{s}</div>)}</div></div>}
                    {languages.length > 0 && <div className="rbp-cr-section"><h2>{t('rbLanguages') || 'Languages'}</h2><table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>{languages.map((l, i) => <tr key={i}><td style={{ fontSize: '11px', fontWeight: 600, color: '#1e293b', padding: '4px 8px 4px 0', width: '70px', verticalAlign: 'middle' }}>{l.name}</td><td style={{ verticalAlign: 'middle', padding: '4px 0' }}><div style={{ height: '5px', backgroundColor: '#E5E7EB', borderRadius: '3px', overflow: 'hidden' }}><div style={{ height: '100%', width: `${l.level}%`, backgroundColor: '#7C3AED', borderRadius: '3px' }} /></div></td><td style={{ fontSize: '10px', color: '#64748b', width: '36px', textAlign: 'right', verticalAlign: 'middle', padding: '4px 0 4px 6px' }}>{l.level}%</td></tr>)}</tbody></table></div>}
                    {hobbies.length > 0 && <div className="rbp-cr-section"><h2>{t('rbHobbies') || 'Hobbies'}</h2><div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>{hobbies.map((h, i) => { const hs = HOBBY_STYLES[i % HOBBY_STYLES.length]; return <div key={i} style={{ height: '28px', lineHeight: '28px', paddingLeft: '14px', paddingRight: '14px', backgroundColor: hs.bg, color: hs.color, borderRadius: '20px', fontSize: '11px', fontWeight: 700, textAlign: 'center', border: `1px solid ${hs.border}`, boxSizing: 'border-box' }}>{h}</div>; })}</div></div>}
                    {socialLinks.length > 0 && <div className="rbp-cr-section"><h2>{t('rbSocialLinks') || 'Links'}</h2><table style={{ borderCollapse: 'collapse' }}><tbody>{socialLinks.map((link, i) => { const type = detectSocial(link.url); const SIcon = SOCIAL_ICONS[type].icon; return <tr key={i}><td style={{ padding: '3px 8px 3px 0', verticalAlign: 'middle' }}><SIcon size={12} style={{ color: SOCIAL_ICONS[type].color }} /></td><td style={{ fontSize: '11px', color: '#475569', verticalAlign: 'middle', padding: '3px 0' }}>{link.text}</td></tr>; })}</tbody></table></div>}
                  </div>
                </div>
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumeBuilder;
